import {
  and,
  arrayRemove,
  arrayUnion,
  collection,
  CollectionReference,
  deleteField,
  doc,
  DocumentData,
  documentId,
  FieldValue,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  Query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  QueryFieldFilterConstraint,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from '@firebase/firestore';
import {
  ChatDocument,
  ChatInput,
  ChatMessageDocument,
  ChatMessageInput,
  chatMessagesSchema,
  chatSchema,
  SupportState,
} from '@models/chat';
import type { OrderDocument } from '@models/order';
import {
  db,
  FirebaseCallable,
  functions,
  httpsCallable,
  storage,
} from '@util/firebase';
import { logError } from '@util/logError';
import { ADMIN_UID, hasSupportPermission, SUPPORT_UIDS } from '@util/support';
import type { ContactUsForm } from 'app/contact/components/ContactUs';
import { getProductsByIds } from '@util/firestore/products';
import { getPublicUserDocs } from '@util/firestore/users';
import type { ChatSearchTerms } from './chatSearch';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { ProductDocument } from 'models/product';
import type { SMSDocument } from 'models/sms';
import { UserDocument } from 'models/user';

export const messagesRef = collection(
  db,
  'messages'
) as CollectionReference<ChatDocument>;

export type ChatFilterType =
  | 'all'
  | 'starred'
  | 'unread'
  | 'flagged'
  | 'offers'
  | 'offers_pending'
  | 'offers_accepted'
  | 'offers_declined'
  | 'offers_expired'
  | 'buying'
  | 'selling'
  | 'support'
  | 'source'
  | 'messages'
  | 'experts';

export type MainFilters =
  | 'all'
  | 'starred'
  | 'unread'
  | 'offers'
  | 'buy'
  | 'flagged'
  | 'sell'
  | 'support'
  | 'agent'
  | 'offers_all'
  | 'offers_pending'
  | 'offers_pending_received'
  | 'offers_pending_sent'
  | 'offers_accepted'
  | 'offers_declined'
  | 'offers_expired'
  | 'support_all'
  | 'support_new'
  | 'support_awaiting_customer'
  | 'support_awaiting_support'
  | 'support_resolved'
  | 'support_cancelled'
  | 'support_awaiting_seller'
  | 'support_internal_review'
  | 'source'
  | 'source_all'
  | 'source_phone'
  | 'source_claims'
  | 'source_ai';

export const MAIN_FILTERS = {
  all: 'All',
  starred: 'Starred',
  unread: 'Unread',
  offers: 'Offers',
  buy: 'Buying',
  sell: 'Selling',
  support: 'Support',
  agent: 'Agent',
  flagged: 'Flagged',
  offers_all: 'All',
  offers_pending: 'All Pending',
  offers_pending_received: 'Pending: Received',
  offers_pending_sent: 'Pending: Sent',
  offers_accepted: 'Accepted',
  offers_declined: 'Declined',
  offers_expired: 'Expired',
  support_all: 'All',
  support_new: 'New Ticket',
  support_awaiting_customer: 'Awaiting Customer',
  support_awaiting_support: 'Awaiting Support',
  support_resolved: 'Resolved',
  support_cancelled: 'Cancelled',
  support_awaiting_seller: 'Awaiting Seller',
  support_internal_review: 'Internal Review',
  source: 'Source',
  source_all: 'All',
  source_phone: 'Phone Support',
  source_claims: 'Claims',
  source_ai: 'AI Support',
} as const;

export type OfferFilters =
  | 'all'
  | 'buy'
  | 'sell'
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'expired';

export type OfferDirection = 'received' | 'sent';

// Pending tabs that narrow to who is waiting on whom
export const OFFER_DIRECTION_BY_FILTER: Partial<
  Record<MainFilters, OfferDirection>
> = {
  offers_pending_received: 'received',
  offers_pending_sent: 'sent',
};

// Whoever put the current terms on the table, which is the author of the newest message
// carrying an offer_amount. Scans from the end so the messages array is never copied.
export function getLastOfferSenderId(
  messages: ChatDocument['messages'] | undefined
): string | undefined {
  if (!messages) return undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].offer_amount != null) return messages[i].uid;
  }
  return undefined;
}

// A thread with no offer message yet cannot have been sent by anyone, so it stays in the
// received bucket where the seller can still see it
export function matchesOfferDirection({
  chat,
  uid,
  direction,
}: {
  chat: ChatDocument;
  uid: string | undefined;
  direction: OfferDirection | undefined;
}): boolean {
  if (!direction || !uid) return true;
  const isSent = getLastOfferSenderId(chat.messages) === uid;
  return direction === 'sent' ? isSent : !isSent;
}

export function getChatFilterConstraint({
  uid,
  mainFilter,
}: {
  uid: string;
  mainFilter: MainFilters;
}): QueryFieldFilterConstraint | undefined {
  if (mainFilter === 'unread') {
    return where('unread.' + uid, '==', true);
  }

  if (mainFilter.startsWith('offers')) {
    return where('is_offer', '==', true);
  }

  if (mainFilter === 'buy') {
    return where('buyer_id', '==', uid);
  }

  if (mainFilter === 'sell') {
    return where('seller_id', '==', uid);
  }

  if (mainFilter === 'support' || mainFilter === 'support_all') {
    return where('support_state', '!=', null);
  }

  if (mainFilter === 'support_new') {
    return where('support_state', '==', 0);
  }

  if (mainFilter === 'support_awaiting_customer') {
    return where('support_state', '==', 1);
  }

  if (mainFilter === 'support_awaiting_support') {
    return where('support_state', '==', 2);
  }

  if (mainFilter === 'support_resolved') {
    return where('support_state', '==', 3);
  }

  if (mainFilter === 'support_cancelled') {
    return where('support_state', '==', 4);
  }

  if (mainFilter === 'support_awaiting_seller') {
    return where('support_state', '==', 5);
  }

  if (mainFilter === 'support_internal_review') {
    return where('support_state', '==', 6);
  }

  if (mainFilter === 'source' || mainFilter === 'source_all') {
    return where('support_state', '!=', null);
  }

  if (mainFilter === 'source_phone') {
    return where('support_source', '==', 'phone_support');
  }

  if (mainFilter === 'source_claims') {
    return where('support_source', '==', 'claims');
  }

  if (mainFilter === 'source_ai') {
    return where('support_source', '==', 'ai_support');
  }

  if (mainFilter === 'agent') {
    return where('support_assigned', '==', 'ai-agent');
  }

  if (mainFilter === 'flagged') {
    return where('flagged', '==', true);
  }

  if (mainFilter === 'starred') {
    return where('favorited', 'array-contains', uid);
  }

  return undefined;
}

export function getChatsQuery({
  userDoc,
  mainFilter,
  numOfChats = 20,
}: {
  userDoc: UserDocument | null | undefined;
  mainFilter: MainFilters;
  numOfChats?: number;
}) {
  let q = query(messagesRef);
  if (!userDoc?.uid) return q;

  const hasSupportRole = !!userDoc.roles?.support;
  const shouldFilterByUser =
    (!hasSupportRole || mainFilter === 'buy' || mainFilter === 'sell') &&
    mainFilter !== 'starred' &&
    mainFilter !== 'agent';

  if (shouldFilterByUser) {
    q = query(q, where('uids', 'array-contains', userDoc.uid));
  }

  const adminUid = process.env.NEXT_PUBLIC_SUPPORT_ID ?? '';

  if (mainFilter === 'unread' && hasSupportRole && adminUid !== userDoc.uid) {
    // show all support unread chats
    q = query(
      q,
      or(
        ...Object.keys(SUPPORT_UIDS).map((id) =>
          where(`unread.${id}`, '==', true)
        )
      )
    );
  } else {
    const filterConstraint = getChatFilterConstraint({
      uid: userDoc.uid,
      mainFilter,
    });

    if (filterConstraint) {
      q = query(q, filterConstraint);
    }
  }

  if (!['unread', 'starred'].includes(mainFilter)) {
    q = query(q, orderBy('last_time', 'desc'), limit(numOfChats));
  }

  return q;
}

export async function getChatsByIds(
  ids: string[],
  userDoc: UserDocument | null | undefined
) {
  try {
    const hasSupportRole = !!userDoc?.roles?.support;
    const idsToQuery = ids.slice(0, 30);

    if (hasSupportRole) {
      const q = query(messagesRef, where(documentId(), 'in', idsToQuery));
      const { docs } = await getDocs(q);

      return docs.map((d) => d.data());
    } else if (userDoc?.uid) {
      const q = query(
        messagesRef,
        where(documentId(), 'in', idsToQuery),
        where('uids', 'array-contains', userDoc.uid)
      );
      const { docs } = await getDocs(q);

      return docs.map((d) => d.data());
    } else {
      return [];
    }
  } catch (_e) {
    const results = await Promise.allSettled(
      ids.map((id) => getDoc(doc(messagesRef, id)))
    );
    const chats: ChatDocument[] = [];
    results.forEach((r) => {
      if (r.status === 'fulfilled') {
        const chat = r.value.data();
        if (chat) chats.push(chat);
      }
    });
    return chats;
  }
}

export function getChatsByUidQuery(
  userDoc: UserDocument,
  variant?: 'messages' | 'offers' | 'orderDetail' | 'experts'
) {
  const constraints = [];
  const hasSupportRole = !!userDoc?.roles?.support;

  if (!hasSupportRole) {
    constraints.push(where('uids', 'array-contains', userDoc.uid));
  }

  if (variant === 'offers') {
    constraints.push(where('is_offer', '==', true));
  }

  constraints.push(orderBy('last_time', 'desc'), limit(50));
  return query(messagesRef, ...constraints);
}

// chats pulled from firestore to search through, since firestore has no
// full text search the matching itself happens client side
export const CHAT_SEARCH_WINDOW_SIZE = 200;

const PUBLIC_USER_CHUNK_SIZE = 50;

// product titles and usernames live outside the chat doc but are what the
// conversation list actually shows, so they have to be resolved to be searchable
export async function getChatSearchTerms(
  chats: ChatDocument[],
  uid: string
): Promise<ChatSearchTerms> {
  const productIds = [
    ...new Set(
      chats.map((chat) => chat.product_id).filter((id): id is string => !!id)
    ),
  ];
  const otherUids = [
    ...new Set(chats.flatMap((chat) => chat.uids).filter((id) => id !== uid)),
  ];

  const userChunks: string[][] = [];
  for (let i = 0; i < otherUids.length; i += PUBLIC_USER_CHUNK_SIZE) {
    userChunks.push(otherUids.slice(i, i + PUBLIC_USER_CHUNK_SIZE));
  }

  const [products, userChunkResults] = await Promise.all([
    getProductsByIds(productIds).catch((e) => {
      logError(e as Error);
      return [] as ProductDocument[];
    }),
    Promise.all(
      userChunks.map((chunk) =>
        getPublicUserDocs(chunk).catch((e) => {
          logError(e as Error);
          return [];
        })
      )
    ),
  ]);

  const titleByProductId = new Map(products.map((p) => [p.id, p.title]));
  const usernameByUid = new Map(
    userChunkResults.flat().map((u) => [u.uid, u.username])
  );

  const terms: ChatSearchTerms = {};
  for (const chat of chats) {
    terms[chat.id] = [
      chat.product_id ? titleByProductId.get(chat.product_id) : undefined,
      ...chat.uids
        .filter((id) => id !== uid)
        .map((id) => usernameByUid.get(id)),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }
  return terms;
}

// widens the pool beyond the page currently rendered in the list so search
// reaches conversations the user has not scrolled to yet
export async function getChatsForSearchWindow({
  userDoc,
  mainFilter,
}: {
  userDoc: UserDocument | null | undefined;
  mainFilter: MainFilters;
}): Promise<ChatDocument[]> {
  if (!userDoc?.uid) return [];
  try {
    const snap = await getDocs(
      getChatsQuery({
        userDoc,
        mainFilter,
        numOfChats: CHAT_SEARCH_WINDOW_SIZE,
      })
    );
    return snap.docs.map((doc) => doc.data());
  } catch (e) {
    logError(e as Error);
    return [];
  }
}

export async function getChatsByOrderOrCaseNumber(
  searchQuery: string,
  userDoc: UserDocument | null | undefined
): Promise<ChatDocument[]> {
  const digits = searchQuery.replaceAll('#', '').trim();
  const asNumber = Number(digits);
  if (!userDoc?.uid || !digits || !Number.isFinite(asNumber)) return [];

  const numberFilter = or(
    where('order_num', '==', asNumber),
    where('case_num', '==', asNumber)
  );
  const q: Query<ChatDocument, DocumentData> = userDoc.roles?.support
    ? query(messagesRef, numberFilter, limit(50))
    : query(
        messagesRef,
        and(where('uids', 'array-contains', userDoc.uid), numberFilter),
        limit(50)
      );

  try {
    const snap = await getDocs(q);
    return snap.docs.map((doc) => doc.data());
  } catch (e) {
    logError(e as Error);
    return [];
  }
}

export function getChatById(chat_id: string) {
  return doc(messagesRef, chat_id);
}

export async function getChatDocumentById(chat_id: string) {
  const snapshot = await getDoc(getChatById(chat_id));
  const chat: ChatDocument | undefined = snapshot.data();
  if (!chat) return null;
  return chat;
}

export async function getChatByCaseNumber(case_num: number) {
  const q = query(messagesRef, where('case_num', '==', case_num), limit(1));
  const snap = await getDocs(q);
  const doc = snap.docs[0]?.data() as ChatDocument | undefined;
  return doc;
}

export async function getChatsForOrderDetails(
  order: OrderDocument,
  uid: string
) {
  const promises = [getChatsByOrderId(order.id, uid)];
  if (order.product_ids && order.seller_arr)
    promises.push(
      getChatsByProductIds(order.product_ids, order.seller_arr, order.buyer_id)
    );
  const allChats = (await Promise.all(promises)).flat();
  const uniqueChatsMap = new Map<string, ChatDocument>();
  for (const chat of allChats) {
    if (!uniqueChatsMap.has(chat.id)) {
      uniqueChatsMap.set(chat.id, chat);
    }
  }
  const chats = Array.from(uniqueChatsMap.values()).sort(
    (a, b) => b.last_time - a.last_time
  );
  return chats;
}

export async function getChatsForSellerAdminControls(uid: string, l?: number) {
  try {
    const q = l
      ? query(
          messagesRef,
          where('uids', 'array-contains', uid),
          orderBy('last_time', 'desc'),
          limit(l)
        )
      : query(
          messagesRef,
          where('uids', 'array-contains', uid),
          orderBy('last_time', 'desc')
        );
    const docs = await getDocs(q);
    return docs.docs.map((doc) => doc.data());
  } catch (e) {
    logError(e as Error);
    return [];
  }
}
async function getChatsByProductIds(
  products: string[],
  seller_ids: string[],
  buyer_id: string
) {
  try {
    const q = query(
      messagesRef,
      where('product_id', 'in', products),
      where('buyer_id', '==', buyer_id)
    );
    const docs = await getDocs(q);
    return docs.docs
      .map((doc) => doc.data())
      .filter((chat) => seller_ids.includes(chat.seller_id));
  } catch (e) {
    logError(e as Error);
    return [];
  }
}

export async function getChatsByOrderId(order_id: string, uid: string) {
  const isSupport = hasSupportPermission(uid);
  const constraints: QueryConstraint[] = [where('order_id', '==', order_id)];
  if (!isSupport) constraints.push(where('uids', 'array-contains', uid));
  constraints.push(orderBy('last_time', 'desc'));
  const q = query(messagesRef, ...constraints);
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getSMSByOrderId(order_id: string) {
  const smsRef = collection(db, 'sms') as CollectionReference<SMSDocument>;
  const q = query(smsRef, where('order_id', '==', order_id));
  const { docs } = await getDocs(q);
  const sms = docs.map((doc) => doc.data());
  return sms.sort((a, b) => b.updated - a.updated);
}

export async function getTotalAdminBuyerChats() {
  try {
    const q = query(
      messagesRef,
      where('seller_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('last_time', '>=', 1688011200000), // launch date
      orderBy('last_time', 'desc')
    );
    const autoQ = query(
      messagesRef,
      where('seller_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('last_time', '>=', 1688011200000), // launch date,
      where('is_auto', '==', true),
      orderBy('last_time', 'desc')
    );
    const [count, autoCount] = await Promise.all([
      getCountFromServer(q),
      getCountFromServer(autoQ),
    ]);
    return count.data().count - autoCount.data().count;
  } catch (e) {
    logError(e as Error);
    return 0;
  }
}

export async function getTotalAdminSellerChats() {
  try {
    const q = query(
      messagesRef,
      where('buyer_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('last_time', '>=', 1688011200000), // launch date
      orderBy('last_time', 'desc')
    );
    const autoQ = query(
      messagesRef,
      where('buyer_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
      where('last_time', '>=', 1688011200000), // launch date,
      where('is_auto', '==', true),
      orderBy('last_time', 'desc')
    );
    const [count, autoCount] = await Promise.all([
      getCountFromServer(q),
      getCountFromServer(autoQ),
    ]);
    return count.data().count - autoCount.data().count;
  } catch (e) {
    logError(e as Error);
    return 0;
  }
}

export async function loadMoreUserChats({
  uid,
  after,
  filter,
}: {
  uid: string;
  after?: string | number;
  filter?: QueryFieldFilterConstraint;
}) {
  const baseConstraints = [
    where('uids', 'array-contains', uid),
    orderBy('last_time', 'desc'),
    ...(after ? [startAfter(after)] : []),
    limit(50),
  ];

  const constraints = filter ? [...baseConstraints, filter] : baseConstraints;

  const q = query(messagesRef, ...constraints);

  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getTwentyChats(lastChatTime?: number) {
  if (lastChatTime) {
    return await getNextTwenty(lastChatTime);
  }
  const q = query(messagesRef, orderBy('last_time', 'desc'), limit(20));
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getNextTwenty(lastChatTime: number) {
  const q = query(
    messagesRef,
    orderBy('last_time', 'desc'),
    startAfter(lastChatTime),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getTwentySellerChats(lastChatTime?: number) {
  if (lastChatTime) {
    return await getNextTwentySeller(lastChatTime);
  }
  const q = query(
    messagesRef,
    where('buyer_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('last_time', '>=', 1688011200000), // launch date
    orderBy('last_time', 'desc'),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getNextTwentySeller(lastChatTime: number) {
  const q = query(
    messagesRef,
    where('buyer_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
    orderBy('last_time', 'desc'),
    where('last_time', '>=', 1688011200000), // launch date
    startAfter(lastChatTime),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getTwentyBuyerChats(lastChatTime?: number) {
  if (lastChatTime) {
    return await getNextTwentyBuyer(lastChatTime);
  }
  const q = query(
    messagesRef,
    where('seller_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('last_time', '>=', 1688011200000), // launch date
    orderBy('last_time', 'desc'),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getNextTwentyBuyer(lastChatTime: number) {
  const q = query(
    messagesRef,
    where('seller_id', '==', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('uids', 'array-contains', process.env.NEXT_PUBLIC_SUPPORT_ID),
    where('last_time', '>=', 1688011200000), // launch date
    orderBy('last_time', 'desc'),
    startAfter(lastChatTime),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getTwentyFlaggedChats(lastChatTime?: number) {
  if (lastChatTime) {
    return await getNextTwentyFlagged(lastChatTime);
  }
  const q = query(
    messagesRef,
    where('flagged', '==', true),
    orderBy('last_time', 'desc'),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function getNextTwentyFlagged(lastChatTime: number) {
  const q = query(
    messagesRef,
    where('flagged', '==', true),
    orderBy('last_time', 'desc'),
    startAfter(lastChatTime),
    limit(20)
  );
  const docs = await getDocs(q);
  return docs.docs.map((doc) => doc.data());
}

export async function sendMessageToChat(
  message: ChatMessageDocument | ChatMessageDocument[],
  chat: ChatDocument,
  currUser?: UserDocument,
  product?: ProductDocument | null
) {
  const messages = Array.isArray(message) ? message : [message]; // normalize to array
  const newChat = { ...chat };
  if (!newChat.unread) {
    newChat.unread = {};
  }

  if (currUser?.roles?.support) {
    newChat.unread = {};
    newChat.unread[chat.buyer_id] = true;
    newChat.unread[chat.seller_id] = true;
    newChat.unread[currUser.uid] = false;
    if (newChat.support_state === undefined) {
      newChat.support_state = SupportState.NewTicket;
      newChat.support_assigned = currUser.uid;
    }
  } else if (currUser) {
    const recipient =
      currUser.uid === chat.uids[0] ? chat.uids[1] : chat.uids[0];
    newChat.unread = { [recipient]: true, [currUser.uid]: false };
  }

  if (newChat.messages) {
    newChat.messages.push(...messages);
  } else {
    newChat.messages = messages;
  }
  const now = Date.now();
  messages.forEach((m, i) => {
    m.created_at = now + i;
  });
  newChat.last_time = now;

  const docRef = doc(messagesRef, chat.id);
  const res = chatSchema.safeParse(newChat);
  if (res.success) {
    await setDoc(docRef, res.data);
  } else {
    logError(res.error, `Zod Error Chat:${chat.id} ${res.error.toString()}`);
    await setDoc(docRef, newChat);
  }
  return newChat;
}

// The backend reuses an existing chat between the same buyer/seller/product
// (appending the messages) and assigns the real case number to support chats.
export async function createChat(chat: ChatInput): Promise<ChatDocument> {
  const isSupportChat =
    !!chat.case_num || (!!chat.uids?.includes(ADMIN_UID) && !chat.is_auto);
  const { data } = await httpsCallable<
    { chat_input: ChatInput },
    { chat_output: ChatDocument }
  >(
    functions,
    FirebaseCallable.createChat
  )({
    chat_input: {
      ...chat,
      // any truthy case_num flags a support chat; the backend replaces it
      ...(isSupportChat && { case_num: 1 }),
    },
  });
  return data.chat_output;
}

export async function getChat(chat_id: string) {
  const docRef = doc(messagesRef, chat_id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data() as ChatDocument;
  } else {
    return null;
  }
}

// also update any notifications for this chat
export async function markAsReadNotifications(chat_id: string, uid: string) {
  try {
    const notificationRef = collection(db, 'notifications');
    const q = query(
      notificationRef,
      where('uid', '==', uid),
      where('web_url', '==', `/dashboard/messages/${chat_id}`)
    );
    const docs = await getDocs(q);
    const notifications = docs.docs.map((doc) => doc.data());
    await Promise.all(
      notifications.map((n) =>
        updateDoc(doc(notificationRef, n.id), { read: true })
      )
    );
  } catch (e) {
    logError(e);
  }
}

export async function markAsRead(chat: ChatDocument, uid: string) {
  if (hasSupportPermission(uid)) return markAsReadSupport(chat, uid);
  if (!chat.unread?.[uid]) return; // nothing to clear — already read
  const docRef = doc(messagesRef, chat.id);
  const key = `unread.${uid}`;
  await updateDoc(docRef, { [key]: false });
  void markAsReadNotifications(chat.id, uid);
}

export async function markAsReadSupport(chat: ChatDocument, uid: string) {
  const allSupportIdsRead = Object.keys(SUPPORT_UIDS).every(
    (id) => !chat.unread?.[id]
  );
  if (allSupportIdsRead) return;
  const updateObj = Object.keys(SUPPORT_UIDS).reduce((acc, id) => {
    acc[`unread.${id}`] = false;
    return acc;
  }, {} as Record<string, boolean>);
  const docRef = doc(messagesRef, chat.id);
  await updateDoc(docRef, updateObj);
  void markAsReadNotifications(chat.id, uid);
}

export async function markAsUnread(chat_id: string, uid: string) {
  const docRef = doc(messagesRef, chat_id);
  const key = `unread.${uid}`;
  await updateDoc(docRef, { [key]: true });
}

export async function flagChat(chat_id: string, flagged: boolean) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, { flagged });
}

export async function getFlaggedChats() {
  try {
    const q = query(
      messagesRef,
      where('flagged', '==', true),
      orderBy('last_time', 'desc')
    );
    const docs = await getDocs(q);
    return docs.docs.map((doc) => doc.data());
  } catch (e) {
    logError(e as Error);
    return [];
  }
}

export async function hideChat(chat_id: string, uid: string) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, {
    uids: arrayRemove(uid),
    [`unread.${uid}`]: deleteField(),
  });
}

export async function hideAllChatsForUser(uid: string) {
  const chats = await getChatsForSellerAdminControls(uid);
  await Promise.all(
    chats.map((chat) =>
      Promise.all(
        (chat.uids || [])
          .filter((id) => id !== uid)
          .map((id) => hideChat(chat.id, id))
      )
    )
  );
}

export async function removeCaseNumber(chat_id: string, uid: string) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, {
    case_num: deleteField(),
    support_state: SupportState.Cancelled,
    [`unread.${uid}`]: deleteField(),
    uids: arrayRemove(uid),
  });
}

export async function resolveChat(chat_id: string, uid: string) {
  const docRef = doc(messagesRef, chat_id);
  const autoMessage: ChatMessageInput = {
    uid: process.env.NEXT_PUBLIC_SUPPORT_ID!,
    content:
      'This chat has been resolved. Please open a new support ticket if you have a new issue.',
    created_at: Date.now(),
    is_auto: true,
  };
  const autoMessageParsed = chatMessagesSchema.parse(autoMessage);
  await updateDoc(docRef, {
    support_state: SupportState.Resolved,
    [`unread.${uid}`]: deleteField(),
    [`unread.${process.env.NEXT_PUBLIC_SUPPORT_ID}`]: deleteField(),
    uids: arrayRemove(uid),
    messages: arrayUnion(autoMessageParsed),
  });
}

export async function deleteMessage(
  chat_id: string,
  message: ChatMessageDocument
) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, { messages: arrayRemove(message) });
}

export async function uploadChatImage(
  data: Blob,
  user_id: string,
  chat_id: string
) {
  const path = `${user_id}/messages/${chat_id}/${Date.now()}`;
  const imagesRef = ref(storage, path);
  const res = await uploadBytes(imagesRef, data);
  try {
    const download_url = await getDownloadURL(res.ref);
    return { download_url, path: res.metadata.fullPath };
  } catch (e) {
    if ((e as Error).message.includes('does not exist')) {
      const prefix = `https://storage.googleapis.com/${process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET}/`;
      const fullPath = `${prefix}${res.metadata.fullPath}_1920x2560`;
      return { download_url: fullPath, path: res.metadata.fullPath };
    }
    throw e;
  }
}

export async function contactUs(form: ContactUsForm) {
  const res = await httpsCallable<ContactUsForm, { data: 'success' }>(
    functions,
    FirebaseCallable.contactUs
  )(form);
  return res.data.data;
}

export async function subscribeToList(email: string, listId?: string) {
  const res = await httpsCallable<{ email: string; listId?: string }>(
    functions,
    FirebaseCallable.subscribeToList
  )({ email, listId });
  return res.data;
}

export async function isSubscribedToList({
  email,
  listId,
}: {
  email: string;
  listId: string;
}) {
  try {
    const res = await httpsCallable<{ email: string; listId: string }>(
      functions,
      FirebaseCallable.isSubscribed
    )({ email, listId });
    return res.data;
  } catch (e) {
    logError(e as Error);
    return false;
  }
}

export async function getUnreadMessagesCount(
  uid?: string,
  isSupport?: boolean
) {
  if (!uid) return 0;
  const q = isSupport
    ? query(
        messagesRef,
        or(
          ...Object.keys(SUPPORT_UIDS).map((id) =>
            where(`unread.${id}`, '==', true)
          )
        )
      )
    : query(
        messagesRef,
        where(`unread.${uid}`, '==', true),
        where('uids', 'array-contains', uid)
      );

  const docs = await getCountFromServer(q);
  return docs.data().count ?? 0;
}

export async function getSupportTicketsBetween(start: Date, end: Date) {
  try {
    const q = query(
      messagesRef,
      where('created_at', '>=', start.getTime()),
      where('created_at', '<=', end.getTime())
    );
    const docs = await getDocs(q);
    const chats = docs.docs.map((doc) => doc.data());
    return chats.filter((chat) => chat.case_num);
  } catch (e) {
    logError(e as Error);
    return [];
  }
}

export async function updateChat(
  chat_id: string,
  data: Partial<ChatDocument> | Partial<Record<keyof ChatDocument, FieldValue>>
) {
  const docRef = doc(messagesRef, chat_id);
  await updateDoc(docRef, data);
}

export async function getChats(
  args: Pick<Partial<ChatDocument>, 'id' | 'buyer_id' | 'seller_id'>
) {
  const clauses = [];
  if (args.id) clauses.push(where('id', '==', args.id));
  if (args.buyer_id) clauses.push(where('buyer_id', '==', args.buyer_id));
  if (args.seller_id) clauses.push(where('seller_id', '==', args.seller_id));
  const q = query(messagesRef, ...clauses);
  const snap = await getDocs(q);
  return snap.docs.map((doc) => doc.data() as ChatDocument);
}

export async function getChatForContact({
  fields,
  userDoc,
}: {
  userDoc: UserDocument;
  fields: {
    product_id?: string;
    order_id?: string;
    recipient_uid: string;
  };
}) {
  const baseConstraints = [where('uids', 'array-contains', userDoc?.uid)];

  if (fields.product_id) {
    baseConstraints.push(where('product_id', '==', fields.product_id));
  } else {
    baseConstraints.push(where('product_id', '==', ''));
  }

  if (fields.order_id) {
    baseConstraints.push(where('order_id', '==', fields.order_id));
  } else {
    baseConstraints.push(where('order_id', '==', ''));
  }

  let userFilter: QueryFieldFilterConstraint | QueryCompositeFilterConstraint;

  if (fields.recipient_uid) {
    userFilter = or(
      and(
        where('seller_id', '==', userDoc?.uid),
        where('buyer_id', '==', fields.recipient_uid)
      ),
      and(
        where('buyer_id', '==', userDoc?.uid),
        where('seller_id', '==', fields.recipient_uid)
      )
    );
  } else {
    userFilter = or(
      where('seller_id', '==', userDoc?.uid),
      where('buyer_id', '==', userDoc?.uid)
    );
  }

  const q = query(messagesRef, and(...baseConstraints, userFilter));
  const snap = await getDocs(q);
  const results = snap.docs.map((doc) => doc.data() as ChatDocument);

  return results;
}
